import * as Sentry from "@sentry/react"
import { isFetchError, runFetch } from "fetcher"
import { Struct, assert, string, type } from "superstruct"
import { getConfig } from "../config"

const TransferTokenResponse = type({
  TransferKey: string(),
})

export const accessTokenToTransferKey = async (
  accessToken: string | undefined
) => {
  if (typeof accessToken !== "string" || accessToken.trim() === "") {
    throw new Error("Missing access token")
  }

  const { authUrl } = await getConfig()
  const data = await runFetch({
    baseUrl: authUrl,
    path: "/api/v1/sessions/transfer",
    method: "PUT",
    transform: (data) => validate(TransferTokenResponse, data),
    headers: { Authorization: `Bearer ${accessToken}` },
  })
  return data.TransferKey
}

const TokenResponse = type({
  Token: string(),
})

export const transferKeyToAccessToken = async (
  transferKey: string
): Promise<string | undefined> => {
  const { authUrl } = await getConfig()
  try {
    const data = await runFetch({
      baseUrl: authUrl,
      method: "GET",
      path: `/api/v1/sessions/transfer/${transferKey}`,
      transform: (data) => validate(TokenResponse, data),
    })
    return removeBearerPrefix(data.Token)
  } catch (error) {
    // If this endpoint throws an error, then the natural consequence
    // is that we don't have the access token. We should avoid crashing
    // the entire app and instead let the parent proceed as they would
    // if they never had the transfer key at all. We achieve that
    // by returning undefined instead of the access token.

    // However it would be good to know what exactly happened.
    // Known scenarios when the endpoint will fail, but we don't want to
    // send a Sentry log, are listed below:
    const sentryIgnoredErrors = [
      { text: "Transfer Key Already Used", status: 400 },
      { text: "Invalid Transfer Key", status: 400 },
    ]
    const isSentryIgnoredError =
      isFetchError(error) &&
      sentryIgnoredErrors.find(
        (ignoredError) =>
          ignoredError.text === error.text &&
          ignoredError.status === error.status
      )

    if (isSentryIgnoredError) {
      console.warn(`Known error: ${error.text}`)
    } else {
      // We can encounter other scenarios, and I am not entirely sure
      // what those are, so just to be safe I will log them to Sentry
      // to determine if they should be handled or also ignored.
      Sentry.setTag("feature", "transferKeyToAccessToken")
      Sentry.captureException(error)
    }

    // In any case, we don't have the access token, so return undefined
    return undefined
  }
}

const validate = async <T>(schema: Struct<T>, data: unknown) => {
  assert(data, schema)
  return data
}

const removeBearerPrefix = (value: string) => {
  return value.replace(/bearer\s*/i, "").trim()
}
